import { Flame, Leaf, BookOpen, Users, ExternalLink, Send, Mail, LogOut, Bell, BellOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useDailyNotifications } from "@/hooks/useDailyNotifications";

const VALUES = [
  { title: "Trasformazione", desc: "Ogni pratica è un seme di cambiamento.", accent: "#e07818", Icon: Flame },
  { title: "Natura",          desc: "La saggezza degli elementi come maestra.", accent: "#2e9020", Icon: Leaf },
  { title: "Saggezza",        desc: "Antiche conoscenze rese accessibili.",     accent: "#941a7c", Icon: BookOpen },
  { title: "Comunità",        desc: "Camminare insieme verso l'interno.",       accent: "#1535a0", Icon: Users },
];

export default function ChiSiamo() {
  const { user, logout } = useAuth();
  const { enabled: notifEnabled, supported: notifSupported, permission: notifPermission, toggle: toggleNotif } = useDailyNotifications();
  return (
    <div className="px-6 pt-10" data-testid="chisiamo-screen">
      <header className="mb-6">
        <p className="font-body text-[11px] tracking-[0.4em] uppercase text-[#1535a0]">
          Chi Siamo
        </p>
        <h1 className="font-serif-display text-[36px] leading-[1.05] tracking-tight text-[#2e2820] mt-2">
          La nostra <span className="italic">missione</span>
        </h1>
      </header>

      {/* Mission */}
      <section
        className="bg-[#f9f5ef] rounded-[18px] p-6 shadow-[0_8px_24px_-16px_rgba(46,40,32,0.25)]"
        style={{ borderTop: "2px solid #1535a0" }}
        data-testid="chisiamo-mission"
      >
        <p className="font-serif-display italic text-[22px] leading-[1.35] text-[#2e2820]/90">
          “Love Shaman Way nasce dal desiderio di portare la saggezza delle tradizioni sciamaniche nella vita quotidiana di ogni persona”
        </p>
      </section>

      {/* Values 2x2 */}
      <section className="mt-8" data-testid="chisiamo-values">
        <h2 className="font-serif-display text-2xl text-[#2e2820] mb-4">I nostri valori</h2>
        <div className="grid grid-cols-2 gap-4">
          {VALUES.map(({ title, desc, accent, Icon }) => (
            <div
              key={title}
              data-testid={`value-${title.toLowerCase()}`}
              className="bg-[#f9f5ef] rounded-[18px] p-4 transition-transform hover:-translate-y-1"
              style={{ borderTop: `2px solid ${accent}` }}
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center mb-3"
                style={{ background: `${accent}1a` }}
              >
                <Icon strokeWidth={1.5} className="w-5 h-5" style={{ color: accent }} />
              </div>
              <div className="font-serif-display text-xl text-[#2e2820] leading-none">{title}</div>
              <p className="font-body text-xs text-[#7a6f65] mt-2 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Percorsi block */}
      <section
        className="mt-8 bg-[#2e2820] text-[#f9f5ef] rounded-[18px] p-6"
        data-testid="chisiamo-percorsi"
      >
        <h2 className="font-serif-display text-2xl mb-3">I nostri percorsi</h2>
        <p className="font-body text-sm leading-relaxed text-[#f9f5ef]/85">
          Attraverso pratiche quotidiane, meditazioni, rituali e viaggi sciamanici, potrai imparare a conoscere te stesso più in profondità e trasformare la tua vita un passo alla volta.
        </p>
      </section>

      {/* External links */}
      <section className="mt-8 flex flex-col gap-3 mb-4" data-testid="chisiamo-links">
        <a
          href="https://loveshamanway.it"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="link-website"
          className="flex items-center justify-between bg-[#f9f5ef] rounded-[18px] px-5 py-4 border border-[#ede7df] transition-all hover:-translate-y-0.5"
          style={{ borderTop: "2px solid #e07818" }}
        >
          <div>
            <div className="font-body text-[11px] tracking-[0.3em] uppercase text-[#7a6f65]">Sito web</div>
            <div className="font-serif-display text-xl text-[#2e2820] leading-tight mt-0.5">loveshamanway.it</div>
          </div>
          <ExternalLink strokeWidth={1.5} className="w-5 h-5 text-[#e07818]" />
        </a>

        <a
          href="https://t.me/loveshamanwaycamminanelcuore"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="link-telegram"
          className="flex items-center justify-between bg-[#f9f5ef] rounded-[18px] px-5 py-4 border border-[#ede7df] transition-all hover:-translate-y-0.5"
          style={{ borderTop: "2px solid #0088cc" }}
        >
          <div>
            <div className="font-body text-[11px] tracking-[0.3em] uppercase text-[#7a6f65]">Unisciti su</div>
            <div className="font-serif-display text-xl text-[#2e2820] leading-tight mt-0.5">Telegram</div>
          </div>
          <Send strokeWidth={1.5} className="w-5 h-5" style={{ color: "#0088cc" }} />
        </a>

        <a
          href="https://www.loveshamanway.it/iscriviti-alla-newsletter"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="link-newsletter"
          className="flex items-center justify-between bg-[#f9f5ef] rounded-[18px] px-5 py-4 border border-[#ede7df] transition-all hover:-translate-y-0.5"
          style={{ borderTop: "2px solid #e07818" }}
        >
          <div>
            <div className="font-body text-[11px] tracking-[0.3em] uppercase text-[#7a6f65]">Iscriviti alla</div>
            <div className="font-serif-display text-xl text-[#2e2820] leading-tight mt-0.5">Newsletter</div>
          </div>
          <Mail strokeWidth={1.5} className="w-5 h-5 text-[#e07818]" />
        </a>
      </section>

      {/* Account / notifications / logout */}
      <section className="mt-6 mb-4" data-testid="chisiamo-account">
        {user && (
          <p className="font-body text-xs tracking-[0.2em] uppercase text-[#7a6f65] mb-3 ml-1">
            Sei connesso come <span className="text-[#2e2820]">{user.nome || user.email}</span>
          </p>
        )}

        {/* Notification toggle */}
        <div
          data-testid="notif-toggle-row"
          className="flex items-center justify-between bg-[#f9f5ef] rounded-[18px] px-5 py-4 border border-[#ede7df] mb-3"
          style={{ borderTop: "2px solid #e07818" }}
        >
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {notifEnabled ? (
              <Bell strokeWidth={1.5} className="w-5 h-5 text-[#e07818] mt-0.5 shrink-0" />
            ) : (
              <BellOff strokeWidth={1.5} className="w-5 h-5 text-[#7a6f65] mt-0.5 shrink-0" />
            )}
            <div className="min-w-0">
              <div className="font-serif-display text-lg text-[#2e2820] leading-tight">
                Scintilla del Giorno
              </div>
              <div className="font-body text-xs text-[#7a6f65] mt-0.5 leading-snug">
                {notifSupported
                  ? notifPermission === "denied"
                    ? "Permessi notifiche negati dal browser."
                    : "Notifiche giornaliere alle 8:00."
                  : "Non supportato su questo dispositivo."}
              </div>
            </div>
          </div>
          <button
            data-testid="notif-toggle-btn"
            type="button"
            disabled={!notifSupported || notifPermission === "denied"}
            onClick={() => toggleNotif(!notifEnabled)}
            aria-pressed={notifEnabled}
            className={`relative w-12 h-7 rounded-full transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed ${
              notifEnabled ? "bg-[#e07818]" : "bg-[#ede7df]"
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${
                notifEnabled ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        </div>

        <button
          data-testid="logout-btn"
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 bg-[#f9f5ef] border border-[#ede7df] text-[#2e2820] font-body font-medium rounded-full py-3 px-5 transition-all hover:bg-[#ede7df] active:scale-[0.98]"
        >
          <LogOut strokeWidth={1.5} className="w-4 h-4 text-[#941a7c]" />
          Esci
        </button>
      </section>
    </div>
  );
}
